import { useEffect } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

/**
 * Records a model view (generator_run_views) whenever the opened model is
 * actually on the user's screen: once the viewer has rendered it while the
 * tab is visible, and again each time the user returns to a tab with the
 * model open. Complements the community-models GET tracking, which only
 * covers click navigations - websocket-hydrated generations never fetch it.
 *
 * Logged-in users only; the server dedups repeat pings (24-hour cache +
 * unique index), so re-pinging on tab return is harmless.
 */
function useModelViewPing({ enabled = true } = {}) {
  const pageConfig = usePageConfig()
  const runId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const { postData } = useApiPost()
  const isLoggedIn = Boolean(pageConfig?.isCurrentUser)

  useEffect(() => {
    if (!enabled || runId == null || !isLoggedIn) {
      return undefined
    }

    const ping = () => {
      postData({ path: `/api/internal/generator/runs/${runId}/view` })
    }

    if (document.visibilityState === 'visible') {
      ping()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ping()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [enabled, runId, isLoggedIn, postData])
}

export default useModelViewPing
